import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Award,
  ShieldCheck,
  GraduationCap,
  FileBadge,
  Briefcase,
  Users,
  CheckCircle2,
  ScrollText,
} from "lucide-react";
import Certification from "@/components/landing/Certification";
import Contact from "@/components/landing/Contact";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import CookieConsent from "@/components/landing/CookieConsent";

const WHATSAPP_URL =
  "https://wa.me/393773660476?text=Ciao%20Michele%2C%20vorrei%20informazioni%20sul%20percorso%20di%20certificazione%20A.C.D.T.";
const ACDT_URL = "https://www.assoacdt.com";

const steps = [
  {
    n: "01",
    title: "Candidatura",
    text:
      "Compila il modulo di interesse o contattami su WhatsApp. Valutiamo insieme requisiti, obiettivi e tempi.",
    icon: ScrollText,
  },
  {
    n: "02",
    title: "Percorso formativo",
    text:
      "Accedi al programma A.C.D.T. che integra metodo, cybersicurezza, leadership e sostenibilità digitale.",
    icon: GraduationCap,
  },
  {
    n: "03",
    title: "Esame & attestato",
    text:
      "Superi la prova finale e ricevi l'attestato ufficiale + iscrizione al Registro come Consulente Digitale.",
    icon: FileBadge,
  },
  {
    n: "04",
    title: "Esercizio della professione",
    text:
      "Operi come professionista certificato L. 4/2013 con tutela giuridica e riconoscimento sul mercato.",
    icon: Briefcase,
  },
];

const includes = [
  "Iscrizione al Registro A.C.D.T. (Legge 4/2013)",
  "Attestato ufficiale Consulente Digitale, Tecnologico e di Sostenibilità",
  "Materiali del Metodo di Mare (libro + dispense + checklist operative)",
  "Accesso alla community privata dei certificati",
  "Aggiornamento continuo annuale incluso nel primo anno",
  "Possibilità di candidatura come Educatore A.C.D.T. (formatore)",
];

const idealFor = [
  {
    title: "Professionisti digitali",
    text:
      "Consulenti, freelance, marketer, sviluppatori che vogliono una qualifica riconosciuta e un metodo replicabile.",
  },
  {
    title: "Manager & imprenditori",
    text:
      "Per guidare la trasformazione digitale della propria azienda con criteri di sicurezza e sostenibilità.",
  },
  {
    title: "Educatori & formatori",
    text:
      "Insegnanti e formatori che vogliono certificarsi per erogare percorsi A.C.D.T. nei propri contesti.",
  },
  {
    title: "Studenti & laureati",
    text:
      "Chi ha terminato gli studi e cerca una professione del futuro, tutelata e in forte crescita.",
  },
];

const faqs = [
  {
    q: "Cos'è A.C.D.T. e perché è importante la Legge 4/2013?",
    a: "A.C.D.T. è l'associazione professionale dei Consulenti Digitali, Tecnologici e di Sostenibilità. La Legge 4/2013 disciplina le professioni non organizzate in ordini e albi: l'iscrizione al Registro A.C.D.T. ti dà riconoscimento ufficiale, tutela giuridica e diritto di fregiarti del titolo.",
  },
  {
    q: "Serve una laurea per certificarsi?",
    a: "No. La certificazione valuta competenze ed esperienza: il percorso è aperto sia a chi ha titoli accademici sia a chi viene dal campo. In sede di candidatura analizziamo il tuo profilo e ti indichiamo il percorso più adatto.",
  },
  {
    q: "Quanto tempo richiede l'intero percorso?",
    a: "Il percorso standard si completa in 3-6 mesi tra formazione, project work e prova finale. Esistono percorsi accelerati per chi ha già esperienza consolidata. Ti propongo un piano su misura.",
  },
  {
    q: "Posso esercitare subito dopo la certificazione?",
    a: "Sì. Dopo l'iscrizione al Registro A.C.D.T. sei autorizzato a esercitare come professionista certificato L. 4/2013 e a comunicarlo a clienti e partner con la formula prevista dalla legge.",
  },
  {
    q: "Quanto costa il percorso?",
    a: "L'investimento varia in base al profilo di partenza e al percorso scelto. Richiedi una call gratuita: in 20 minuti ti propongo il piano più adatto e il preventivo trasparente.",
  },
];

export default function CertificazionePage() {
  return (
    <main
      data-testid="certificazione-page-root"
      className="min-h-screen bg-[#F9F8F6] text-[#0B192C]"
    >
      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F9F8F6]/85 border-b border-[#E5E1DA]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="font-serif-display text-xl tracking-tight"
            data-testid="certificazione-brand"
          >
            Metodo<span className="text-[#E89B6C]">·</span>di
            <span className="text-[#E89B6C]">·</span>Mare
          </Link>
          <div className="flex items-center gap-3 md:gap-6">
            <Link
              to="/"
              data-testid="certificazione-back-home"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#0E7C8F]"
            >
              <ArrowLeft size={14} />
              <span className="hidden sm:inline">Vai al libro</span>
              <span className="sm:hidden">Libro</span>
            </Link>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="certificazione-nav-cta"
              className="px-5 py-3 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#1E3557] transition-colors"
            >
              Candidati ora
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-20 md:pb-28 overflow-hidden bg-gradient-to-br from-[#0B192C] via-[#173F5F] to-[#0E7C8F] text-[#F9F8F6]">
        <div
          aria-hidden
          className="absolute -top-32 right-0 w-[50%] h-[80%] opacity-30 blur-3xl pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, #E89B6C 0%, transparent 70%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#E89B6C]/40 bg-[#E89B6C]/10 mb-6">
              <Award size={14} className="text-[#E89B6C]" strokeWidth={1.4} />
              <span className="overline text-[#E89B6C]">
                Certificazione professionale · L. 4/2013
              </span>
            </div>
            <h1 className="font-serif-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight">
              Diventa
              <br />
              professionista
              <br />
              <em className="text-[#E89B6C]">digitale certificato.</em>
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl font-light leading-relaxed text-[#F9F8F6]/80">
              Il percorso ufficiale per diventare{" "}
              <em>
                Consulente ed Educatore Digitale, Tecnologico e di Sostenibilità
              </em>{" "}
              tramite{" "}
              <a
                href={ACDT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E89B6C] underline underline-offset-4 hover:text-[#F9F8F6]"
                data-testid="hero-acdt-link"
              >
                A.C.D.T.
              </a>{" "}
              — l'associazione regolamentata dalla Legge 4/2013. Una
              professione riconosciuta, tutelata e in forte crescita.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="certificazione-hero-cta-primary"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#E89B6C] text-[#0B192C] text-sm uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-all"
              >
                Candidati ora
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="certificazione-hero-cta-whatsapp"
                className="inline-flex items-center gap-3 px-7 py-4 border border-[#F9F8F6]/30 text-[#F9F8F6] text-sm uppercase tracking-[0.2em] hover:bg-[#F9F8F6]/10 transition-colors"
              >
                <WhatsAppIcon size={16} className="text-[#25D366]" />
                WhatsApp diretto
              </a>
            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl">
              <Stat n="L.4/13" l="Legge di riferimento" />
              <Stat n="3-6 mesi" l="Durata percorso" />
              <Stat n="100%" l="Riconoscimento" />
              <Stat n="A.C.D.T." l="Registro ufficiale" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Detailed Certification block (reused) */}
      <Certification />

      {/* Process steps */}
      <section className="py-24 md:py-32 border-t border-[#E5E1DA] bg-[#F4E8D6]/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-16">
            <p className="overline">Il percorso</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
              Quattro tappe.
              <br />
              <em className="text-[#0E7C8F]">Una professione.</em>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E1DA] border border-[#E5E1DA]">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="bg-[#FFFFFF] p-8 md:p-10 relative"
                  data-testid={`certificazione-step-${i}`}
                >
                  <p className="font-serif-display text-5xl md:text-6xl text-[#E89B6C]">
                    {s.n}
                  </p>
                  <Icon
                    size={20}
                    strokeWidth={1.4}
                    className="text-[#0E7C8F] mt-6"
                  />
                  <h3 className="font-serif-display text-2xl mt-3 leading-tight text-[#0B192C]">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-base font-light leading-relaxed text-[#3A4F6B]">
                    {s.text}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-24 md:py-32 border-t border-[#E5E1DA]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <p className="overline">Cosa ricevi</p>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-[1.05]">
              Tutto quello che ti serve per
              <br />
              <em className="text-[#0E7C8F]">esercitare davvero.</em>
            </h2>
            <p className="mt-6 text-lg font-light text-[#3A4F6B] max-w-md">
              Non solo un attestato: un sistema completo per partire,
              esercitare e crescere come professionista certificato.
            </p>
          </div>
          <div className="md:col-span-7">
            <ul className="space-y-5">
              {includes.map((inc, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="flex items-start gap-4 pb-5 border-b border-[#E5E1DA] last:border-0"
                  data-testid={`certificazione-include-${i}`}
                >
                  <CheckCircle2
                    size={22}
                    strokeWidth={1.4}
                    className="text-[#E89B6C] flex-shrink-0 mt-0.5"
                  />
                  <span className="text-lg font-light text-[#0B192C]">
                    {inc}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Ideal for */}
      <section className="py-24 md:py-32 border-t border-[#E5E1DA] bg-[#D6E5EC]/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl mb-16">
            <p className="overline">Per chi è</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
              Adatto a chi
              <br />
              <em className="text-[#0E7C8F]">vuole alzare il livello.</em>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-[#E5E1DA] border border-[#E5E1DA]">
            {idealFor.map((it, i) => (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-[#FFFFFF] p-8 md:p-10"
                data-testid={`certificazione-ideal-${i}`}
              >
                <Users
                  size={22}
                  strokeWidth={1.3}
                  className="text-[#E89B6C]"
                />
                <h3 className="font-serif-display text-2xl mt-4 leading-tight">
                  {it.title}
                </h3>
                <p className="mt-3 text-base font-light leading-relaxed text-[#3A4F6B]">
                  {it.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 border-t border-[#E5E1DA]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="overline text-center">Domande frequenti</p>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-[1.05] text-center">
            Tutto quello che
            <br />
            <em className="text-[#0E7C8F]">vuoi sapere.</em>
          </h2>

          <div className="mt-16 divide-y divide-[#E5E1DA] border-y border-[#E5E1DA]">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="group py-6 cursor-pointer"
                data-testid={`certificazione-faq-${i}`}
              >
                <summary className="flex items-start justify-between gap-6 list-none">
                  <span className="font-serif-display text-xl md:text-2xl leading-snug text-[#0B192C]">
                    {f.q}
                  </span>
                  <span className="font-serif-display text-2xl text-[#E89B6C] transition-transform group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-base md:text-lg font-light leading-relaxed text-[#3A4F6B]">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA strip */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-[#0B192C] via-[#1E3557] to-[#0E7C8F] text-[#F9F8F6]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
          <ShieldCheck
            size={36}
            strokeWidth={1.2}
            className="text-[#E89B6C] mx-auto mb-6"
          />
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            La tua professione
            <br />
            <em className="text-[#E89B6C]">parte oggi.</em>
          </h2>
          <p className="mt-6 text-lg font-light text-[#F9F8F6]/80 max-w-2xl mx-auto">
            Candidati ora: una valutazione gratuita del tuo profilo e un piano
            chiaro per arrivare alla certificazione A.C.D.T.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="certificazione-final-cta"
            className="group mt-10 inline-flex items-center gap-3 px-10 py-5 bg-[#E89B6C] text-[#0B192C] text-sm uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-all"
          >
            Candidati ora
            <ArrowRight
              size={18}
              className="transition-transform group-hover:translate-x-1"
            />
          </a>
        </div>
      </section>

      <Contact />
      <Footer />
      <WhatsAppFloat />
      <CookieConsent />
    </main>
  );
}

function Stat({ n, l }) {
  return (
    <div className="border-t border-[#F9F8F6]/20 pt-4">
      <p className="font-serif-display text-3xl md:text-4xl text-[#E89B6C]">
        {n}
      </p>
      <p className="overline mt-1 text-[#F9F8F6]/70">{l}</p>
    </div>
  );
}
